import { X } from 'lucide-react';
import { useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Bouton, type VarianteBouton } from './Bouton';
import styles from './Dialogue.module.css';

const FOCALISABLES =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ProprietesDialogue {
  ouvert: boolean;
  titre: ReactNode;
  onFermer: () => void;
  children: ReactNode;
  actions?: ReactNode;
  /** Description lue par les lecteurs d'écran à l'ouverture. */
  description?: string;
}

/**
 * Fenêtre modale : élément dialog natif (le reste de la page devient inerte), focus piégé
 * dans la fenêtre, fermeture par Échap, retour du focus à l'élément d'origine.
 */
export function Dialogue({ ouvert, titre, onFermer, children, actions, description }: ProprietesDialogue) {
  const ref = useRef<HTMLDialogElement>(null);
  const origine = useRef<Element | null>(null);
  const idTitre = useId();
  const idDescription = useId();

  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (ouvert && !d.open) {
      origine.current = document.activeElement;
      if (typeof d.showModal === 'function') d.showModal();
      else d.setAttribute('open', '');
      const premier = d.querySelector<HTMLElement>('[data-focus-initial]') ?? d.querySelector<HTMLElement>(FOCALISABLES);
      premier?.focus();
    } else if (!ouvert && d.open) {
      if (typeof d.close === 'function') d.close();
      else d.removeAttribute('open');
      if (origine.current instanceof HTMLElement) origine.current.focus();
    }
  }, [ouvert]);

  const pieger = (e: KeyboardEvent<HTMLDialogElement>) => {
    if (e.key !== 'Tab' || !ref.current) return;
    const elements = [...ref.current.querySelectorAll<HTMLElement>(FOCALISABLES)];
    if (elements.length === 0) return;
    const premier = elements[0] as HTMLElement;
    const dernier = elements[elements.length - 1] as HTMLElement;
    if (e.shiftKey && document.activeElement === premier) {
      e.preventDefault();
      dernier.focus();
    } else if (!e.shiftKey && document.activeElement === dernier) {
      e.preventDefault();
      premier.focus();
    }
  };

  return (
    <dialog
      ref={ref}
      className={styles.dialogue}
      aria-labelledby={idTitre}
      aria-describedby={description ? idDescription : undefined}
      aria-modal="true"
      onCancel={(e) => {
        e.preventDefault();
        onFermer();
      }}
      onKeyDown={pieger}
    >
      {ouvert ? (
        <>
          <div className={styles.entete}>
            <h2 id={idTitre}>{titre}</h2>
            <button type="button" className={styles.fermer} onClick={onFermer} aria-label="Fermer la fenêtre">
              <X aria-hidden strokeWidth={1.75} width={20} height={20} />
            </button>
          </div>
          <div className={styles.corps}>
            {description ? <p id={idDescription}>{description}</p> : null}
            {children}
          </div>
          {actions ? <div className={styles.actions}>{actions}</div> : null}
        </>
      ) : null}
    </dialog>
  );
}

export interface ProprietesConfirmation {
  ouvert: boolean;
  titre: string;
  message: ReactNode;
  libelleConfirmer: string;
  variante?: VarianteBouton;
  enCours?: boolean;
  onConfirmer: () => void;
  onAnnuler: () => void;
  children?: ReactNode;
  confirmationDesactivee?: boolean;
}

/** Dialogue de confirmation : le bouton d'annulation reçoit le focus initial. */
export function DialogueConfirmation({
  ouvert,
  titre,
  message,
  libelleConfirmer,
  variante = 'principal',
  enCours,
  onConfirmer,
  onAnnuler,
  children,
  confirmationDesactivee,
}: ProprietesConfirmation) {
  return (
    <Dialogue
      ouvert={ouvert}
      titre={titre}
      onFermer={onAnnuler}
      actions={
        <>
          <Bouton variante="secondaire" onClick={onAnnuler} data-focus-initial>
            Annuler
          </Bouton>
          <Bouton variante={variante} onClick={onConfirmer} chargement={enCours} disabled={confirmationDesactivee}>
            {libelleConfirmer}
          </Bouton>
        </>
      }
    >
      <div>{message}</div>
      {children}
    </Dialogue>
  );
}

export interface ProprietesConfirmationIntegree {
  /** Libellé du bouton qui déclenche la demande de confirmation. */
  libelle: string;
  question: ReactNode;
  libelleConfirmer: string;
  onConfirmer: () => void;
  enCours?: boolean;
  variante?: VarianteBouton;
  petit?: boolean;
  disabled?: boolean;
  children?: ReactNode;
  confirmationDesactivee?: boolean;
}

/** Action dangereuse confirmée dans la page, sans fenêtre (charte : bouton danger). */
export function ConfirmationIntegree({
  libelle,
  question,
  libelleConfirmer,
  onConfirmer,
  enCours,
  variante = 'danger',
  petit,
  disabled,
  children,
  confirmationDesactivee,
}: ProprietesConfirmationIntegree) {
  const [demande, setDemande] = useState(false);
  const zone = useRef<HTMLDivElement>(null);
  const declencheur = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (demande) zone.current?.querySelector<HTMLElement>('[data-focus-initial]')?.focus();
  }, [demande]);

  if (!demande) {
    return (
      <Bouton ref={declencheur} variante={variante} petit={petit} disabled={disabled} onClick={() => setDemande(true)}>
        {libelle}
      </Bouton>
    );
  }
  return (
    <div className={styles.integree} ref={zone} role="group" aria-label={libelle}>
      <p>{question}</p>
      {children}
      <div className={styles.integreeActions}>
        <Bouton variante="secondaire" petit={petit} onClick={() => setDemande(false)} data-focus-initial>
          Annuler
        </Bouton>
        <Bouton variante={variante} petit={petit} chargement={enCours} onClick={onConfirmer} disabled={confirmationDesactivee}>
          {libelleConfirmer}
        </Bouton>
      </div>
    </div>
  );
}
